import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import {
  apiService,
  AppRole,
  RolePerson,
} from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { useSearch } from "../../contexts/SearchContext";
import { InputIcon } from "primereact/inputicon";
import { IconField } from "primereact/iconfield";

const RolesAdmin: React.FC = () => {
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(false);
  const { searchTerm: filter, setSearchTerm: setFilter } = useSearch();
  const [modalVisible, setModalVisible] = useState(false);
  const [editRole, setEditRole] = useState<AppRole | null>(null);
  const [formRoleKey, setFormRoleKey] = useState("");
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");

  const [peopleModalVisible, setPeopleModalVisible] = useState(false);
  const [peopleRole, setPeopleRole] = useState<AppRole | null>(null);
  const [rolePeople, setRolePeople] = useState<RolePerson[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);

  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const res = await apiService.getRoles();
    if (res.success) setRoles(res.data);
    else toast.showError("Failed", res.message || "Unable to load roles");
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () =>
      roles.filter((role) => {
        const text =
          `${role.role_key} ${role.name} ${role.description}`.toLowerCase();
        return text.includes(filter.toLowerCase());
      }),
    [roles, filter]
  );

  const openNew = () => {
    setEditRole(null);
    setFormRoleKey("");
    setFormName("");
    setFormDescription("");
    setModalVisible(true);
  };

  const openEdit = (role: AppRole) => {
    setEditRole(role);
    setFormRoleKey(role.role_key);
    setFormName(role.name);
    setFormDescription(role.description || "");
    setModalVisible(true);
  };

  const save = async () => {
    if (!formRoleKey.trim()) {
      toast.showWarn("Validation", "Role key is required");
      return;
    }
    if (!formName.trim()) {
      toast.showWarn("Validation", "Name is required");
      return;
    }

    if (editRole) {
      const res = await apiService.updateRole(editRole.id, {
        role_key: formRoleKey.trim(),
        name: formName.trim(),
        description: formDescription.trim(),
      });
      if (res.success) {
        toast.showSuccess("Saved", `Role '${res.data.name}' updated`);
        setModalVisible(false);
        await load();
      } else {
        toast.showError("Error", res.message || "Save failed");
      }
      return;
    }

    const res = await apiService.createRole({
      role_key: formRoleKey.trim(),
      name: formName.trim(),
      description: formDescription.trim(),
    });
    if (res.success) {
      toast.showSuccess("Saved", `Role '${res.data.name}' created`);
      setModalVisible(false);
      await load();
    } else {
      toast.showError("Error", res.message || "Save failed");
    }
  };

  const remove = async (role: AppRole) => {
    const res = await apiService.deleteRole(role.id);
    if (res.success) {
      toast.showSuccess("Deleted", `Role '${role.name}' deleted`);
      await load();
    } else {
      toast.showError("Error", res.message || "Delete failed");
    }
  };

  const openPeople = async (role: AppRole) => {
    setPeopleRole(role);
    setPeopleModalVisible(true);
    setPeopleLoading(true);
    const res = await apiService.getRolePeople(role.id);
    if (res.success) setRolePeople(res.data);
    else {
      toast.showError("Failed", res.message || "Unable to load people");
      setRolePeople([]);
    }
    setPeopleLoading(false);
  };

  const formatAssignedAt = (value: string) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  const header = (
    <div className="flex justify-content-between align-items-center">
      <div className="flex align-items-center gap-2">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search roles..."
          />
        </IconField>
      </div>
      <Button label="Add" icon="pi pi-plus" onClick={openNew} />
    </div>
  );

  return (
    <div>
      <DataTable
        value={filtered}
        loading={loading}
        header={header}
        emptyMessage="No roles found"
        className="p-datatable-sm"
        paginator
        rows={10}
      >
        <Column field="role_key" header="Key" sortable />
        <Column field="name" header="Name" sortable />
        <Column field="description" header="Description" />
        <Column
          header="Actions"
          body={(row: AppRole) => (
            <div className="flex gap-2 justify-content-end">
              <Button
                icon="pi pi-users"
                className="p-button-rounded p-button-outlined p-button-sm p-button-info"
                onClick={() => openPeople(row)}
                tooltip="View people"
              />
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-outlined p-button-sm"
                onClick={() => openEdit(row)}
                tooltip="Edit"
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-outlined p-button-sm p-button-danger"
                onClick={() => remove(row)}
                tooltip="Delete"
              />
            </div>
          )}
          style={{ width: "160px" }}
          headerStyle={{ textAlign: "right" }}
        />
      </DataTable>

      <Dialog
        header={editRole ? "Edit Role" : "Add Role"}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: 480 }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="field">
            <label className="block mb-2">Role key</label>
            <InputText
              value={formRoleKey}
              onChange={(e) => setFormRoleKey(e.target.value)}
              className="w-full"
              placeholder="e.g. red_team"
            />
          </div>
          <div className="field">
            <label className="block mb-2">Name</label>
            <InputText
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="field">
            <label className="block mb-2">Description</label>
            <InputTextarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              className="w-full"
              rows={3}
              autoResize
            />
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setModalVisible(false)}
            />
            <Button label="Save" icon="pi pi-check" onClick={save} />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={
          peopleRole
            ? `People with role: ${peopleRole.name}`
            : "People with role"
        }
        visible={peopleModalVisible}
        onHide={() => setPeopleModalVisible(false)}
        style={{ width: 720 }}
        modal
      >
        <DataTable
          value={rolePeople}
          loading={peopleLoading}
          emptyMessage="No people assigned to this role"
          className="p-datatable-sm"
          paginator
          rows={10}
        >
          <Column
            header="Name"
            body={(row: RolePerson) => `${row.name} ${row.surname}`}
            sortable
            sortField="name"
          />
          <Column field="email" header="Email" sortable />
          <Column
            header="Assigned at"
            body={(row: RolePerson) => formatAssignedAt(row.assigned_at)}
          />
          <Column field="assigned_by" header="Assigned by" />
        </DataTable>
      </Dialog>
    </div>
  );
};

export default RolesAdmin;
